import posthog from 'posthog-js';
import supabase from '@/lib/supabase';
import useAuthStore from '@/store/useAuthStore';
import STORAGE_KEYS from '@/constant/storage';
import { getEnv } from '@/config/domains';

// 存储元素开始时间的Map
const elementStartTimes: Map<string, number> = new Map();
// 存储元素交互历史的Map
const elementInteractionHistory: Map<string, Array<{timestamp: number, action: string, data?: any}>> = new Map();

/**
 * 识别用户身份
 * @param userId - 用户唯一标识 (应该是 Supabase 用户 ID)
 * @param properties - 用户属性
 */
export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  // Simplified implementation - just pass through to PostHog
  // The actual identify logic should be handled in useAuthStore to avoid duplication

  try {
    // Only identify if we have a valid userId
    if (!userId || userId === 'null' || userId === 'undefined') {
      console.warn('Invalid userId provided to identifyUser:', userId);
      return;
    }

    // Use the passed userId (should be Supabase ID) to identify PostHog user
    posthog.identify(userId, properties);

    // Record current session ID for event correlation
    const sessionId = posthog.get_session_id();
    if (sessionId) {
      localStorage.setItem(STORAGE_KEYS.CURRENT_SESSION_ID, sessionId);
    }

    console.log(`User ${userId} identified through analytics.identifyUser`);
  } catch (error) {
    console.error('Error identifying user in PostHog:', error);
  }
};

/**
 * 跟踪自定义事件
 * @param eventName - 事件名称
 * @param properties - 事件相关属性
 */
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  // 获取环境和用户信息
  const environmentInfo = getEnvironmentInfo();
  const cachedUserId = useAuthStore.getState().user?.id;
  const sessionId = localStorage.getItem(STORAGE_KEYS.CURRENT_SESSION_ID) || posthog.get_session_id();
  
  // 使用当前缓存的用户ID发送事件
  posthog.capture(eventName, {
    ...properties,
    ...environmentInfo,
    user_id: cachedUserId,
    session_id: sessionId,
    timestamp: new Date().toISOString(),
  });
  
  // 不再在每次事件中检查 Supabase 会话，改为依赖 RootAuthProvider 来同步用户身份
};

/**
 * 重置当前用户身份（登出时调用）
 */
export const resetUser = () => {
  // 记录登出事件
  trackEvent('user_logged_out');
  
  // 移除本地存储的用户相关信息
  localStorage.removeItem('posthog_identifier');
  
  // PostHog 的 reset 方法会重置用户身份
  posthog.reset();
};

/**
 * 跟踪功能使用情况
 * @param featureName - 使用的功能名称
 * @param properties - 功能使用相关属性
 */
export const trackFeatureUsage = (featureName: string, properties?: Record<string, any>) => {
  trackEvent(`feature_used`, {
    feature_name: featureName,
    ...properties,
  });
};

/**
 * 跟踪学习进度事件
 * @param action - 学习动作 (started, completed, etc)
 * @param content - 内容类型 (quest, chapter, lesson)
 * @param properties - 额外属性
 */
export const trackLearningProgress = (
  action: 'started' | 'completed' | 'viewed', 
  content: 'quest' | 'chapter' | 'lesson' | 'element',
  properties: {
    id: string;
    title?: string;
    [key: string]: any;
  }
) => {
  // 如果是章节查看事件，跳过以避免与 trackViewTime 重复
  if (content === 'chapter' && action === 'viewed') {
    // 章节查看事件由 trackViewTime 函数处理，这里不发送事件
    return;
  }

  trackEvent(`${content}_${action}`, {
    [`${content}_id`]: properties.id,
    [`${content}_title`]: properties.title,
    ...properties
  });
};

/**
 * 添加元素交互历史记录
 * @param elementId - 元素ID 
 * @param action - 交互动作
 * @param data - 交互相关数据
 */
const addElementInteractionHistory = (elementId: string, action: string, data?: any) => {
  if (!elementInteractionHistory.has(elementId)) {
    elementInteractionHistory.set(elementId, []);
  }
  
  elementInteractionHistory.get(elementId)?.push({
    timestamp: Date.now(),
    action,
    data
  });
};

/**
 * 跟踪元素中间状态
 * @param elementId - 元素ID
 * @param state - 状态名称
 * @param properties - 状态相关属性
 */
export const trackElementState = (
  elementId: string,
  state: string,
  properties?: Record<string, any>
) => {
  // 记录中间状态
  addElementInteractionHistory(elementId, `state_${state}`, properties);
  
  // 发送事件
  trackEvent('element_state_changed', {
    element_id: elementId,
    state,
    ...properties,
    timestamp: new Date().toISOString()
  });
};

/**
 * 开始跟踪学习元素交互
 * @param elementDetails - 元素详细信息
 */
export const startElementTracking = (elementDetails: {
  element_id: string;
  element_type: string;
  lesson_id: string;
  chapter_id: string;
  quest_id: string;
  element_title?: string;
  position_in_lesson?: number;
  difficulty_level?: string;
  estimated_completion_time?: number;
}) => {
  // 记录元素开始时间
  const startTime = Date.now();
  elementStartTimes.set(elementDetails.element_id, startTime);
  
  // 初始化交互历史
  elementInteractionHistory.set(elementDetails.element_id, [{
    timestamp: startTime,
    action: 'started'
  }]);
  
  // 格式化事件名称为 "element_started [element type]"
  const eventName = `element_started ${elementDetails.element_type}`;
  
  // 记录开始事件（新格式）
  trackEvent(eventName, {
    ...elementDetails,
    start_time: new Date(startTime).toISOString(),
  });
  
  // // 同时发送原始格式的事件（向后兼容）
  // trackEvent('element_started', {
  //   ...elementDetails,
  //   start_time: new Date(startTime).toISOString(),
  // });
};

/**
 * 跟踪元素特定交互事件
 * @param elementId - 元素ID
 * @param interactionType - 交互类型
 * @param properties - 交互相关属性
 */
export const trackElementInteraction = (
  elementId: string,
  interactionType: 'click' | 'input' | 'selection' | 'hover' | 'scroll' | 'keypress' | 'custom',
  properties?: Record<string, any>
) => {
  // 记录交互历史
  addElementInteractionHistory(elementId, interactionType, properties);
  
  // 发送事件
  trackEvent('element_interaction', {
    element_id: elementId,
    interaction_type: interactionType,
    ...properties,
    timestamp: new Date().toISOString()
  });
};

/**
 * 结束跟踪学习元素交互
 * @param elementDetails - 元素详细信息
 * @param additionalProperties - 额外属性
 */
export const endElementTracking = (
  elementDetails: {
    element_id: string;
    element_type: string;
    lesson_id: string;
    chapter_id: string;
    quest_id: string;
    element_title?: string;
    completion_status?: 'completed' | 'skipped' | 'interrupted' | 'failed';
    score?: number;
    attempts?: number;
    completion_percentage?: number;
  }, 
  additionalProperties?: Record<string, any>
) => {
  const endTime = Date.now();
  const startTime = elementStartTimes.get(elementDetails.element_id);
  
  // 获取交互历史
  const interactionHistory = elementInteractionHistory.get(elementDetails.element_id) || [];
  
  // 添加结束事件到交互历史
  addElementInteractionHistory(elementDetails.element_id, 'ended', {
    completion_status: elementDetails.completion_status
  });
  
  if (!startTime) {
    // 如果没有找到开始时间，仍然记录结束事件，但不含时长
    // 格式化事件名称为 "element_ended [element type] [element id]"
    const eventName = `element_ended ${elementDetails.element_type}`;
    
    // 发送新格式的事件
    trackEvent(eventName, {
      ...elementDetails,
      ...additionalProperties,
      end_time: new Date(endTime).toISOString(),
      has_duration: false,
      interaction_count: interactionHistory.length,
      interaction_summary: summarizeInteractions(interactionHistory)
    });
    
    // // 同时发送原始格式的事件（向后兼容）
    // trackEvent('element_completed', {
    //   ...elementDetails,
    //   ...additionalProperties,
    //   end_time: new Date(endTime).toISOString(),
    //   has_duration: false,
    //   interaction_count: interactionHistory.length,
    //   interaction_summary: summarizeInteractions(interactionHistory)
    // });
    
    return;
  }
  
  // 计算花费的时间（毫秒）
  const duration = endTime - startTime;
  const durationInSeconds = Math.round(duration / 1000);
  
  // 计算平均互动频率
  const interactionRate = interactionHistory.length > 0 ? 
    interactionHistory.length / (duration / 1000) : 0;
  
  // 格式化事件名称为 "element_ended [element type] [element id] [duration]"
  const eventName = `element_ended ${elementDetails.element_type} ${durationInSeconds}s`;
  
  const eventProperties = {
    ...elementDetails,
    ...additionalProperties,
    start_time: new Date(startTime).toISOString(),
    end_time: new Date(endTime).toISOString(),
    duration_ms: duration,
    duration_seconds: durationInSeconds,
    has_duration: true,
    interaction_count: interactionHistory.length,
    interaction_rate: interactionRate,
    interaction_summary: summarizeInteractions(interactionHistory),
    idle_time_percentage: calculateIdleTimePercentage(interactionHistory, startTime, endTime)
  };
  
  // 记录结束事件，包含花费时间和互动历史摘要（新格式）
  trackEvent(eventName, eventProperties);
  
  // 同时发送原始格式的事件（向后兼容）
  // trackEvent('element_completed', eventProperties);
  
  // 清除开始时间和交互历史
  elementStartTimes.delete(elementDetails.element_id);
  elementInteractionHistory.delete(elementDetails.element_id);
};

/**
 * 计算交互历史摘要
 * @param interactions - 交互历史记录
 * @returns 交互摘要对象
 */
const summarizeInteractions = (interactions: Array<{timestamp: number, action: string, data?: any}>) => {
  // 统计各种交互类型出现的次数
  const actionCounts: Record<string, number> = {};
  
  interactions.forEach(interaction => {
    if (!actionCounts[interaction.action]) {
      actionCounts[interaction.action] = 0;
    }
    actionCounts[interaction.action]++;
  });
  
  return actionCounts;
};

/**
 * 计算空闲时间百分比
 * @param interactions - 交互历史记录
 * @param startTime - 开始时间
 * @param endTime - 结束时间
 * @returns 空闲时间百分比
 */
const calculateIdleTimePercentage = (
  interactions: Array<{timestamp: number, action: string, data?: any}>,
  startTime: number,
  endTime: number
) => {
  if (interactions.length <= 1) return 100; // 只有开始事件，视为100%空闲
  
  // 计算相邻交互间的时间间隔
  const timeGaps: number[] = [];
  
  // 添加第一个交互前的时间
  if (interactions.length > 0 && interactions[0].timestamp > startTime) {
    timeGaps.push(interactions[0].timestamp - startTime);
  }
  
  // 添加交互之间的时间间隔
  for (let i = 1; i < interactions.length; i++) {
    timeGaps.push(interactions[i].timestamp - interactions[i-1].timestamp);
  }
  
  // 定义空闲阈值为3秒
  const idleThreshold = 3000;
  
  // 计算超过空闲阈值的总时间
  const totalIdleTime = timeGaps
    .filter(gap => gap > idleThreshold)
    .reduce((sum, gap) => sum + gap, 0);
  
  // 计算空闲时间百分比
  const totalDuration = endTime - startTime;
  return Math.round((totalIdleTime / totalDuration) * 100);
};

/**
 * 跟踪内容查看时间
 */
export const trackViewTime = (contentDetails: {
  content_type: 'quest' | 'chapter' | 'lesson';
  content_id: string;
  title?: string;
}) => {
  const contentType = contentDetails.content_type;
  const contentId = contentDetails.content_id;
  const storeKey = `view_start_${contentType}_${contentId}`;
  
  // 记录开始查看时间
  if (!sessionStorage.getItem(storeKey)) {
    sessionStorage.setItem(storeKey, Date.now().toString());
    // 记录开始查看事件
    if (contentType === 'chapter' && contentDetails.title) {
      // 为章节添加标题到事件名
      trackEvent(`chapter_view_started ${contentDetails.title}`, {
        chapter_id: contentId,
        chapter_title: contentDetails.title,
        start_time: new Date().toISOString(),
      });
    } else {
      // 其他内容类型保持不变
      trackEvent(`${contentType}_view_started`, {
        [`${contentType}_id`]: contentId,
        [`${contentType}_title`]: contentDetails.title,
        start_time: new Date().toISOString(),
      });
    }
  }
  
  // 设置一个离开监听器，当用户离开页面时记录持续时间
  if (typeof window !== 'undefined') {
    const recordViewDuration = () => {
      const startTimeStr = sessionStorage.getItem(storeKey);
      if (startTimeStr) {
        const startTime = parseInt(startTimeStr);
        const endTime = Date.now();
        const duration = endTime - startTime;
        const durationInSeconds = Math.round(duration / 1000);
        
        // 记录查看时长事件
        if (contentType === 'chapter' && contentDetails.title) {
          // 为章节添加标题和持续时间到事件名
          trackEvent(`chapter_view_ended ${contentDetails.title} ${durationInSeconds}s`, {
            chapter_id: contentId,
            chapter_title: contentDetails.title,
            start_time: new Date(startTime).toISOString(),
            end_time: new Date(endTime).toISOString(),
            duration_ms: duration,
            duration_seconds: durationInSeconds,
          });
        } else {
          // 其他内容类型保持不变
          trackEvent(`${contentType}_view_ended`, {
            [`${contentType}_id`]: contentId,
            [`${contentType}_title`]: contentDetails.title,
            start_time: new Date(startTime).toISOString(),
            end_time: new Date(endTime).toISOString(),
            duration_ms: duration,
            duration_seconds: durationInSeconds,
          });
        }
        
        // 清除存储
        sessionStorage.removeItem(storeKey);
      }
    };
    
    // 添加页面离开监听器
    window.addEventListener('beforeunload', recordViewDuration);
    
    // 返回清理函数
    return recordViewDuration;
  }
  
  return () => {}; // 无操作函数，用于服务器端
};

/**
 * 跟踪错误事件
 * @param errorType - 错误类型
 * @param errorMessage - 错误信息
 * @param properties - 额外属性
 */
export const trackError = (errorType: string, errorMessage: string, properties?: Record<string, any>) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    ...properties
  });
};

type HttpErrorType = 'network_error' | '4xx_client_error' | '5xx_server_error';

/**
 * 获取 HTTP 错误类型分类
 * @param statusCode - HTTP 状态码
 * @returns 错误类型字符串
 */
const getHttpErrorType = (statusCode: number): HttpErrorType => {
  if (statusCode >= 400 && statusCode < 500) {
    return '4xx_client_error';
  } else if (statusCode >= 500) {
    return '5xx_server_error';
  }
  // 对于非标准状态码（如 3xx），归类为 4xx 客户端错误
  return '4xx_client_error';
};

/**
 * 从 URL 中提取端点路径（去除查询参数和敏感信息）
 * @param url - 完整 URL
 * @returns 清理后的端点路径
 */
const sanitizeEndpoint = (url: string): string => {
  try {
    const urlObj = new URL(url, window.location.origin);
    // 只返回路径部分，不包含查询参数
    return urlObj.pathname;
  } catch {
    // 如果解析失败，返回原始 URL（可能是相对路径）
    return url.split('?')[0];
  }
};

/**
 * 跟踪 API 请求
 * @param endpoint - API 端点
 * @param method - HTTP 方法
 * @param statusCode - HTTP 状态码
 * @param durationMs - 请求耗时（毫秒）
 * @param success - 是否成功
 */
export const trackApiRequest = (
  endpoint: string,
  method: string,
  statusCode: number,
  durationMs: number,
  success: boolean
) => {
  const sanitizedEndpoint = sanitizeEndpoint(endpoint);

  trackEvent('api_request', {
    endpoint: sanitizedEndpoint,
    method: method.toUpperCase(),
    status_code: statusCode,
    duration_ms: durationMs,
    success,
  });
};

/**
 * 跟踪 API 错误
 * @param endpoint - API 端点
 * @param method - HTTP 方法
 * @param statusCode - HTTP 状态码（网络错误时为 0）
 * @param errorType - 错误类型
 * @param errorMessage - 错误信息（可选）
 * @param durationMs - 请求耗时（毫秒，可选）
 */
export const trackApiError = (
  endpoint: string,
  method: string,
  statusCode: number,
  errorType: HttpErrorType,
  errorMessage?: string,
  durationMs?: number
) => {
  const sanitizedEndpoint = sanitizeEndpoint(endpoint);

  trackEvent('api_error', {
    endpoint: sanitizedEndpoint,
    method: method.toUpperCase(),
    status_code: statusCode,
    error_type: errorType,
    error_message: errorMessage,
    duration_ms: durationMs,
  });
};

/**
 * 统一的 API 请求追踪函数 - 用于在 ApiClient 中调用
 * @param url - 请求 URL
 * @param method - HTTP 方法
 * @param response - Response 对象（成功时）
 * @param error - 错误对象（失败时）
 * @param startTime - 请求开始时间戳
 */
export const trackApiCall = (
  url: string,
  method: string,
  response?: Response,
  error?: Error,
  startTime?: number
) => {
  const durationMs = startTime ? Date.now() - startTime : undefined;

  if (error) {
    // 网络错误
    trackApiError(
      url,
      method,
      0,
      'network_error',
      error.message,
      durationMs
    );
    return;
  }

  if (response) {
    // 仅记录错误请求，不再上报成功请求
    if (!response.ok) {
      trackApiError(
        url,
        method,
        response.status,
        getHttpErrorType(response.status),
        undefined,
        durationMs
      );
    }
  }
};

/**
 * 跟踪用户互动
 * @param interactionType - 互动类型
 * @param properties - 相关属性
 */
export const trackInteraction = (
  interactionType: 'click' | 'hover' | 'scroll' | 'play' | 'pause' | 'input',
  properties: {
    component: string;
    element_id?: string;
    button_text?: string;
    input_value?: string;
    div_content?: string;
    svg_description?: string;
    [key: string]: any;
  }
) => {
  // 对于按钮点击，添加按钮文本到事件名称
  if (interactionType === 'click' && properties.component === 'button' && properties.button_text) {
    trackEvent(`clicked button with text "${properties.button_text}"`, {
      interaction_type: interactionType,
      ...properties
    });
  } 
  // 对于div点击，添加内容到事件名称
  else if (interactionType === 'click' && properties.component === 'div' && properties.div_content) {
    trackEvent(`clicked div with content "${properties.div_content}"`, {
      interaction_type: interactionType,
      ...properties
    });
  }
  // 对于SVG点击，添加描述到事件名称
  else if (interactionType === 'click' && properties.component === 'svg' && properties.svg_description) {
    trackEvent(`clicked svg ${properties.svg_description}`, {
      interaction_type: interactionType,
      ...properties
    });
  }
  // 对于输入框输入，添加输入内容到事件名称
  else if (interactionType === 'input' && properties.component === 'input' && properties.input_value) {
    trackEvent(`typed into input: ${properties.input_value}`, {
      interaction_type: interactionType,
      ...properties
    });
  }
  else {
    // 其他交互保持不变
    trackEvent(`ui_interaction`, {
      interaction_type: interactionType,
      ...properties
    });
  }
};

/**
 * 跟踪游戏/测验活动
 * @param action - 活动动作
 * @param gameType - 游戏/测验类型
 * @param properties - 额外属性
 */
export const trackGameActivity = (
  action: 'started' | 'completed' | 'scored',
  gameType: string,
  properties?: Record<string, any>
) => {
  trackEvent(`game_${action}`, {
    game_type: gameType,
    ...properties
  });
};

/**
 * 跟踪资源消耗
 * @param resourceType - 资源类型
 * @param action - 动作
 * @param properties - 额外属性
 */
export const trackResourceUsage = (
  resourceType: 'heart' | 'coin' | 'credit',
  action: 'earned' | 'spent' | 'purchased',
  properties?: Record<string, any>
) => {
  trackEvent(`resource_${action}`, {
    resource_type: resourceType,
    ...properties
  });
};

/**
 * 开始会话录制
 */
export const startSessionRecording = () => {
  posthog.startSessionRecording();
};

/**
 * 获取当前环境信息
 */
export const getEnvironmentInfo = () => {
  if (typeof window === 'undefined') return { environment: 'server', hostname: '' };

  const hostname = window.location.hostname;
  const environment = getEnv(hostname);

  return {
    environment,
    hostname,
    current_url: window.location.href
  };
}; 
